/**
 * ч.1296 — звернення до комісії ДПС щодо ПН/РК, реєстрацію якої зупинено (СМКОР):
 * «Повідомлення про подання пояснень та копій документів» (J/F1312603), «додаткові
 * пояснення» на запит комісії (J/F1307801), «Скарга на рішення про відмову» (J/F1313205).
 * Копії документів — вкладення цього документа (з сайту), кожна йде окремим файлом
 * J/F1360102 у пакеті; підпис КЕП і відправка — у Кабінеті ДПС (чернетка зʼявляється там
 * після збереження). Рішення комісії приходить квитанцією і переносить стан на ПН/РК.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  erpnAppeals, KIND_LABELS, RESULT_LABELS, TEXT_MAX,
  type ErpnAppeal, type ErpnAppealCheck, type ErpnAppealKind, type ErpnAttachmentMeta,
  type ErpnLetter, type ErpnSignerKind, type ServerAttachment,
} from '@/api/erpnAppeals';
import { ai as aiApi } from '@/api/ai';
import { taxInvoicesOutgoing, type TaxInvoiceOutgoingListItem } from '@/api/taxInvoices';
import { attachments as attachmentsApi } from '@/api/attachments';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { SUB_STATUS_UK } from '@/shared/components/ErpnAppealsCard';
import ServerDocsPicker, { serverDocLabel } from '@/shared/components/ServerDocsPicker';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const DOC_TYPE = 'doc.erpn_appeals';

interface FormState {
  kind: ErpnAppealKind;
  document_date: string;
  explanation_text: string;
  registration_number: string;
  decision_number: string;
  decision_date: string;
  court_appealed: boolean;
  video_participation: boolean;
  video_person: string;
  signer_kind: ErpnSignerKind;
  signer_inn: string;
  signer_name: string;
  comment: string;
  meta: Record<string, ErpnAttachmentMeta>;
  /** ч.1298: бухгалтерська довідка (лист на бланку) — null, поки не завантажена. */
  letter: ErpnLetter | null;
  /** ч.1299: ПН/РК немає в системі — реквізити руками. */
  manual: boolean;
  m_number: string;
  m_date: string;
  m_is_rk: boolean;
  m_buyer: string;
  m_ipn: string;
  m_base: string;
  m_vat: string;
}

const STOPPED_STATES = ['STOPPED', 'REJECTED', 'BLOCKED', 'ERROR'];
const NOTICE_KEY = 'erpn-appeal-notice';

const hasLetter = (l: ErpnLetter | Record<string, never> | null | undefined): l is ErpnLetter =>
  !!l && Array.isArray((l as ErpnLetter).paragraphs) && (l as ErpnLetter).paragraphs.length > 0;
const paragraphsToText = (p: string[]) => p.join('\n\n');
const textToParagraphs = (t: string) => t.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);

const ERPN_UK: Record<string, string> = {
  STOPPED: 'реєстрацію зупинено', REJECTED: 'відмовлено у реєстрації',
  REGISTERED: 'зареєстровано', SENT: 'на реєстрації', BLOCKED: 'заблоковано',
  ERROR: 'помилка відправки', NOT_SENT: 'не відправлено', DRAFT: 'чернетка подачі',
};
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('uk-UA') : '');
const today = () => new Date().toISOString().slice(0, 10);

export default function ErpnAppealEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const [invoiceId, setInvoiceId] = useState(sp.get('invoice') ?? '');
  const [pickOrgId, setPickOrgId] = useState('');
  const [form, setForm] = useState<FormState>({
    kind: ((sp.get('kind') as ErpnAppealKind | null) ?? 'EXPLANATION'),
    document_date: today(), explanation_text: '', registration_number: '',
    decision_number: '', decision_date: '', court_appealed: false,
    video_participation: false, video_person: '', signer_kind: 'HEAD',
    signer_inn: '', signer_name: '', comment: '', meta: {}, letter: null,
    manual: false, m_number: '', m_date: '', m_is_rk: false, m_buyer: '', m_ipn: '', m_base: '', m_vat: '',
  });
  // ч.1299: вкладення, додані ДО збереження (файли з компʼютера і документи з сервера) —
  // завантажуються одразу після створення документа.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingServer, setPendingServer] = useState<ServerAttachment[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [letterIsTemplate, setLetterIsTemplate] = useState(false);
  const [check, setCheck] = useState<ErpnAppealCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const { availableOrgs } = usePermissionsByOrg('tax.invoice.manage');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const docQ = useQuery({
    queryKey: ['erpn-appeal', id], queryFn: () => erpnAppeals.get(id!), enabled: !isNew,
  });
  const effInvoiceId = isNew ? invoiceId : (docQ.data?.invoice_id ?? '');
  const invQ = useQuery({
    queryKey: ['tax-invoice-outgoing', effInvoiceId],
    queryFn: () => taxInvoicesOutgoing.get(effInvoiceId), enabled: !!effInvoiceId,
  });
  // Новий документ без ПН у адресі: підбір серед ПН/РК організації зі станом
  // «зупинено» / «відмовлено» (саме за ними звертаються до комісії).
  const effPickOrg = pickOrgId || availableOrgs[0]?.id || '';
  const candQ = useQuery({
    queryKey: ['erpn-appeal-candidates', effPickOrg],
    queryFn: () => taxInvoicesOutgoing.list({ organization_id: effPickOrg, limit: 300 }),
    enabled: isNew && !invoiceId && !!effPickOrg,
  });
  // ч.1299 (власник): шукати можна БУДЬ-ЯКУ ПН/РК організації за номером чи покупцем —
  // зупинені / відхилені йдуть першими.
  const candidates = useMemo(() => [...(candQ.data?.items ?? [])].sort((a, b) =>
    Number(STOPPED_STATES.includes(b.erpn_status)) - Number(STOPPED_STATES.includes(a.erpn_status))), [candQ.data]);
  // Новий без ПН у адресі — організація з підбору (підписант прибирається з її картки).
  const orgId = isNew ? (invQ.data?.organization_id ?? effPickOrg) : (docQ.data?.organization_id ?? '');
  const org = orgList.data?.items.find((o) => o.id === orgId);
  const isFop = org?.entity_type === 'FOP';
  const attQ = useQuery({
    queryKey: ['attachments', DOC_TYPE, id, orgId],
    queryFn: () => attachmentsApi.list({ organization_id: orgId, document_type: DOC_TYPE, document_id: id! }),
    enabled: !isNew && !!orgId,
  });

  useEffect(() => {
    if (isNew || !docQ.data) return;
    const d = docQ.data;
    setForm({
      kind: d.kind, document_date: d.document_date.slice(0, 10),
      explanation_text: d.explanation_text ?? '',
      registration_number: d.registration_number != null ? String(d.registration_number) : '',
      decision_number: d.decision_number ?? '', decision_date: d.decision_date ?? '',
      court_appealed: d.court_appealed, video_participation: d.video_participation,
      video_person: d.video_person ?? '', signer_kind: d.signer_kind,
      signer_inn: d.signer_inn ?? '', signer_name: d.signer_name ?? '',
      comment: d.comment ?? '', meta: d.attachment_meta ?? {},
      letter: hasLetter(d.letter) ? d.letter : null,
      manual: !!d.manual_invoice,
      m_number: d.manual_invoice ? (d.invoice_number ?? '') : '',
      m_date: d.manual_invoice ? (d.invoice_date ?? '') : '',
      m_is_rk: !!d.manual_invoice && !!d.invoice_is_correction,
      m_buyer: d.manual_invoice ? (d.contractor_name ?? '') : '',
      m_ipn: d.buyer_ipn ?? '', m_base: d.amount_base ?? '', m_vat: d.amount_vat ?? '',
    });
    setLetterIsTemplate(false);
  }, [isNew, docQ.data]);
  // Повідомлення після створення (сторінка перемонтовується при переході на /:id).
  // (якщо React перевикористав компонент без перемонтування — повідомлення вже стоїть у стані;
  // збережене показуємо лише для СВОГО документа, щоб не спливло на іншій сторінці)
  useEffect(() => {
    try {
      const n = sessionStorage.getItem(NOTICE_KEY);
      if (!n) return;
      const { forId, kind, text } = JSON.parse(n) as { forId: string; kind: string; text: string };
      if (forId !== id) return;
      sessionStorage.removeItem(NOTICE_KEY);
      (kind === 'error' ? setError : setInfo)(text);
    } catch { /* сховище недоступне — без повідомлення */ }
  }, [id]);

  // ч.1298: довідки ще немає — показати шаблон з даних системи (не збережений).
  const letterQ = useQuery({
    queryKey: ['erpn-appeal-letter', id], queryFn: () => erpnAppeals.letter(id!),
    enabled: !isNew && !!docQ.data && !hasLetter(docQ.data.letter),
  });
  useEffect(() => {
    if (!letterQ.data || !docQ.data || hasLetter(docQ.data.letter)) return;
    setForm((f) => (f.letter ? f : { ...f, letter: letterQ.data.letter }));
    setLetterIsTemplate(letterQ.data.is_template);
  }, [letterQ.data, docQ.data]);
  const { availableOrgs: aiOrgs } = usePermissionsByOrg('ai.erpn_explanation.use');

  // Новий документ: підписант за замовчуванням — керівник з картки організації
  // (сервер робить те саме при створенні; тут — щоб блок не виглядав порожнім).
  useEffect(() => {
    if (!isNew || !org) return;
    setForm((f) => (f.signer_inn || f.signer_name) ? f : {
      ...f, signer_inn: (org.director_inn ?? '').replace(/\D/g, '').slice(0, 10),
      signer_name: org.director_name ?? '',
    });
  }, [isNew, org]);

  const canAi = !!orgId && aiOrgs.some((o) => o.id === orgId);
  const balanceQ = useQuery({ queryKey: ['ai-balance'], queryFn: aiApi.balance, enabled: canAi && !isNew });
  const aiAvailable = balanceQ.data
    ? (balanceQ.data.unlimited ? 'без обмежень' : Number(balanceQ.data.available).toFixed(2)) : null;

  const restore = useCallback((v: FormState) => setForm(v), []);
  const draft = useFormDraft(form, restore, {
    ready: isNew ? (!!invoiceId || form.manual) : !!docQ.data, subject: 'цього звернення',
    clearOnAnyMutation: false,
  });

  const sent = !!docQ.data?.submission_status && docQ.data.submission_status !== 'DRAFT';
  const readOnly = sent;
  const isComplaint = form.kind === 'COMPLAINT';
  const textMax = TEXT_MAX[form.kind];
  const textLen = form.explanation_text.length;
  const errText = (e: unknown) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Помилка';
  const toIso = (d: string) => (d ? `${d}T00:00:00Z` : null);
  const manualReady = form.manual && !!form.m_number.trim() && !!form.m_date;
  const manualPayload = () => (form.manual ? {
    invoice_number: form.m_number.trim() || null, invoice_date: form.m_date || null,
    invoice_is_correction: form.m_is_rk, buyer_name: form.m_buyer.trim() || null,
    buyer_ipn: form.m_ipn.replace(/\D/g, '') || null,
    amount_base: form.m_base.replace(',', '.').trim() || null,
    amount_vat: form.m_vat.replace(',', '.').trim() || null,
  } : {});
  const patchPayload = () => ({
    ...manualPayload(),
    document_date: toIso(form.document_date),
    explanation_text: form.explanation_text,
    registration_number: form.registration_number ? Number(form.registration_number) : null,
    decision_number: form.decision_number || null,
    decision_date: form.decision_date || null,
    court_appealed: form.court_appealed,
    video_participation: form.video_participation,
    video_person: form.video_person || null,
    signer_kind: form.signer_kind,
    signer_inn: form.signer_inn || null,
    signer_name: form.signer_name || null,
    comment: form.comment || null,
    ...(form.letter ? { letter: { ...form.letter, date: form.letter.date || null } } : {}),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['erpn-appeal', id] });
    qc.invalidateQueries({ queryKey: ['erpn-appeals'] });
  };
  const create = useMutation({
    mutationFn: async () => {
      const d = await erpnAppeals.create({
        organization_id: orgId, invoice_id: form.manual ? null : invoiceId, kind: form.kind, ...patchPayload(),
      });
      // ч.1299: вкладення, додані до збереження, — одразу після створення
      const errs: string[] = [];
      for (const f of pendingFiles) {
        try {
          await attachmentsApi.upload({ organization_id: orgId, document_type: DOC_TYPE, document_id: d.id, file: f });
        } catch (e) { errs.push(`${f.name}: ${errText(e)}`); }
      }
      if (pendingServer.length) {
        try { await erpnAppeals.attachFromExisting(d.id, pendingServer.map((a) => a.id)); } catch (e) { errs.push(errText(e)); }
      }
      return { d, errs };
    },
    onSuccess: ({ d, errs }) => {
      draft.markSaved(); invalidate();
      const added = pendingFiles.length + pendingServer.length;
      const notice = errs.length
        ? { kind: 'error', text: `Частину вкладень не додано: ${errs.join('; ')}` }
        : added ? { kind: 'info', text: `Звернення створено, вкладень додано: ${added}.` } : null;
      if (notice) {
        (notice.kind === 'error' ? setError : setInfo)(notice.text);
        try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ forId: d.id, ...notice })); } catch { /* сховище недоступне */ }
      }
      setPendingFiles([]); setPendingServer([]);
      navigate(`/erpn-appeals/${d.id}`, { replace: true });
    },
    onError: (e) => setError(errText(e)),
  });
  const fromExistingMut = useMutation({
    mutationFn: (ids: string[]) => erpnAppeals.attachFromExisting(id!, ids),
    onSuccess: (d) => { applyAppeal(d); setInfo('Копії документів з сервера додано до звернення.'); },
    onError: (e) => setError(errText(e)),
  });
  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const ok = Array.from(list).filter((f) => /\.(pdf|jpe?g|png)$/i.test(f.name));
    setPendingFiles((p) => [...p, ...ok.filter((f) => !p.some((x) => x.name === f.name && x.size === f.size))]);
  };
  const update = useMutation({
    mutationFn: () => erpnAppeals.patch(id!, { ...patchPayload(), attachment_meta: form.meta }),
    onSuccess: () => { draft.markSaved(); setCheck(null); invalidate(); },
    onError: (e) => setError(errText(e)),
  });
  const applyAppeal = (d: ErpnAppeal) => {
    setForm((f) => ({
      ...f, letter: hasLetter(d.letter) ? d.letter : f.letter,
      explanation_text: f.explanation_text.trim() ? f.explanation_text : (d.explanation_text ?? ''),
      meta: d.attachment_meta ?? f.meta,
    }));
    setLetterIsTemplate(false);
    qc.setQueryData(['erpn-appeal', id], d);
    qc.invalidateQueries({ queryKey: ['attachments', DOC_TYPE, id] });
    qc.invalidateQueries({ queryKey: ['erpn-appeals'] });
  };
  const saveLetterFirst = async () => {
    if (readOnly) return;
    await erpnAppeals.patch(id!, { ...patchPayload(), attachment_meta: form.meta });
    draft.markSaved();
  };
  const templateMut = useMutation({
    mutationFn: () => erpnAppeals.letterTemplate(id!),
    onSuccess: (d) => { applyAppeal(d); setInfo('Довідку заповнено з даних системи — перевірте текст у квадратних дужках.'); },
    onError: (e) => setError(errText(e)),
  });
  const draftMut = useMutation({
    mutationFn: () => erpnAppeals.letterDraft(id!),
    onSuccess: (r) => {
      applyAppeal(r.appeal);
      qc.invalidateQueries({ queryKey: ['ai-balance'] });
      setInfo(`Довідку склав Oblik+/AI (списано ${Number(r.ai.credits).toFixed(2)} кредитів). `
        + 'Перевірте факти: агент писав лише з даних системи, невідоме позначив у квадратних дужках.');
    },
    onError: (e) => setError(errText(e)),
  });
  const attachMut = useMutation({
    mutationFn: async () => { await saveLetterFirst(); return erpnAppeals.letterAttach(id!); },
    onSuccess: (d) => { applyAppeal(d); setInfo('PDF довідки додано до копій документів — піде у пакет окремим файлом.'); },
    onError: (e) => setError(errText(e)),
  });
  async function previewPdf() {
    setError(null);
    try {
      await saveLetterFirst();
      const blob = await erpnAppeals.letterPdf(id!);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { setError(errText(e)); }
  }
  const setLetter = (patch: Partial<ErpnLetter>) =>
    setForm((f) => ({ ...f, letter: { ...(f.letter ?? { paragraphs: [] }), ...patch } }));
  const remove = useMutation({
    mutationFn: () => erpnAppeals.remove(id!),
    onSuccess: () => { draft.markSaved(); invalidate(); navigate('/erpn-appeals'); },
    onError: (e) => setError(errText(e)),
  });

  async function runCheck() {
    if (isNew) return;
    setChecking(true);
    setError(null);
    try {
      // спершу зберегти правки — перевіряється збережений стан
      if (!readOnly) await erpnAppeals.patch(id!, { ...patchPayload(), attachment_meta: form.meta });
      draft.markSaved();
      setCheck(await erpnAppeals.check(id!));
      invalidate();
    } catch (e) { setError(errText(e)); } finally { setChecking(false); }
  }
  async function downloadXml() {
    try {
      const { files } = await erpnAppeals.files(id!);
      for (const f of files) {
        const bytes = Uint8Array.from(atob(f.xml_base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/xml' }));
        const a = document.createElement('a');
        a.href = url; a.download = f.fname; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      }
    } catch (e) { setError(errText(e)); }
  }
  const setMeta = (attId: string, patch: ErpnAttachmentMeta) =>
    setForm((f) => ({ ...f, meta: { ...f.meta, [attId]: { ...(f.meta[attId] ?? {}), ...patch } } }));

  const railActions = [
    { label: 'перевірити', disabled: isNew || checking,
      title: isNew ? 'Спершу збережіть документ' : 'Реквізити, текст, вкладення і структура кожного файла проти XSD ДПС',
      onClick: () => void runCheck() },
    { label: 'завантажити XML', disabled: isNew,
      title: isNew ? 'Спершу збережіть документ' : 'Файли пакета (повідомлення + копії документів)',
      onClick: () => void downloadXml() },
    { label: 'Кабінет ДПС', disabled: isNew,
      title: isNew ? 'Спершу збережіть документ' : 'Підписати КЕП і відправити пакет',
      onClick: () => navigate('/dps-cabinet') },
    ...(!isNew && !isComplaint ? [{
      label: 'додаткові пояснення', title: 'Нове повідомлення на запит комісії щодо цієї ж ПН/РК',
      onClick: () => navigate(`/erpn-appeals/new?invoice=${effInvoiceId}&kind=ADDITIONAL`),
    }] : []),
    ...(!isNew && form.kind !== 'COMPLAINT' ? [{
      label: 'скарга на відмову', title: 'Якщо комісія відмовила — скарга до комісії центрального рівня (10 робочих днів)',
      onClick: () => navigate(`/erpn-appeals/new?invoice=${effInvoiceId}&kind=COMPLAINT`),
    }] : []),
  ];
  const blocks: DocBlockState[] = [
    { id: 'inv', title: 'ПН/РК і вид звернення', required: isComplaint ? 4 : 2,
      filled: ((effInvoiceId || manualReady) ? 1 : 0) + (form.document_date ? 1 : 0)
        + (isComplaint ? (form.decision_number ? 1 : 0) + (form.decision_date ? 1 : 0) : 0) },
    { id: 'text', title: isComplaint ? 'Причини незгоди з рішенням' : 'Пояснення', required: 1,
      filled: textLen > 0 && textLen <= textMax ? 1 : 0 },
    { id: 'letter', title: 'Бухгалтерська довідка', required: 1,
      filled: form.letter?.attachment_id ? 1 : 0 },
    { id: 'docs', title: 'Копії документів', required: isComplaint ? 0 : 1,
      filled: (attQ.data?.length ?? 0) > 0 ? 1 : 0 },
    { id: 'signer', title: 'Підписант', required: 2,
      filled: (/^\d{10}$/.test(form.signer_inn) ? 1 : 0) + (form.signer_name ? 1 : 0) },
  ];
  const inv = invQ.data;
  const title = isNew ? `Нове звернення: ${KIND_LABELS[form.kind].toLowerCase()}`
    : `${KIND_LABELS[form.kind]} № ${docQ.data?.document_number ?? ''}`;

  const statusCard = !isNew && docQ.data && (
    <div className="docrail__card" data-testid="erpn-appeal-status">
      <div className="docrail__title">Стан подання</div>
      <div style={{ fontSize: '0.85rem' }}>
        {SUB_STATUS_UK[docQ.data.submission_status ?? ''] ?? 'не подано'}
        {docQ.data.form_code && <div style={{ color: 'var(--muted)' }}>форма {docQ.data.form_code}</div>}
      </div>
      {docQ.data.result && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          <b>Рішення комісії:</b> {RESULT_LABELS[docQ.data.result]}
          {docQ.data.result_doc_number && ` № ${docQ.data.result_doc_number}`}
          {docQ.data.result_date && ` від ${fmtDate(docQ.data.result_date)}`}
          {docQ.data.result_text && (
            <div style={{ color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{docQ.data.result_text}</div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            isNew={isNew} postable={false}
            onBackToList={() => navigate('/erpn-appeals')}
            onSave={() => (isNew ? create.mutate() : update.mutate())}
            saveDisabled={readOnly || (isNew && !invoiceId && !manualReady) || create.isPending || update.isPending}
            saveTitle={readOnly ? 'Документ уже подано до ДПС — зміни неможливі'
              : isNew && !invoiceId && !manualReady ? 'Оберіть ПН/РК або вкажіть її номер і дату вручну' : undefined}
            savePending={create.isPending || update.isPending}
            onDelete={() => { confirm('Видалити звернення?').then((ok) => { if (ok) remove.mutate(); }); }}
            deleteDisabled={readOnly} deletePending={remove.isPending}
            deleteTitle={readOnly ? 'Документ уже подано до ДПС — видалити не можна' : undefined}
            railMode extraActions={railActions}
          />
        </div>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.5rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
      {info && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)',
          border: '1px solid var(--badge-green-bd)', borderRadius: 6, padding: '0.5rem 0.75rem',
          marginBottom: '0.5rem', display: 'flex', gap: '0.5rem' }}>
          <span style={{ flex: 1 }}>{info}</span>
          <a style={{ cursor: 'pointer' }} onClick={() => setInfo(null)}>×</a>
        </div>
      )}
      {sent && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
          color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          Подано до ДПС ({SUB_STATUS_UK[docQ.data?.submission_status ?? ''] ?? ''}) — документ лише для перегляду.
          Рішення комісії зʼявиться тут і на самій ПН/РК.
        </div>
      )}
      {check && (
        <div style={{ marginBottom: '0.75rem' }}>
          {check.errors.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)',
              border: '1px solid var(--badge-green-bd)', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
              ✓ Перевірку пройдено — пакет готовий до підпису у Кабінеті ДПС
            </div>
          )}
          {check.errors.map((e, i) => (
            <div key={`e${i}`} style={{ background: 'var(--badge-red-bg)', color: 'var(--badge-red-fg)',
              border: '1px solid var(--badge-red-bd)', borderRadius: 6, padding: '0.4rem 0.75rem', marginBottom: 4 }}>
              ✗ {e.message}
            </div>
          ))}
          {check.warnings.map((w, i) => (
            <div key={`w${i}`} style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
              border: '1px solid var(--badge-amber-bd)', borderRadius: 6, padding: '0.4rem 0.75rem', marginBottom: 4 }}>
              ⚠ {w.message}
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <ServerDocsPicker organizationId={orgId} invoiceId={effInvoiceId || undefined}
          excludeAppealId={isNew ? undefined : id}
          excludeIds={pendingServer.map((a) => a.id)}
          onClose={() => setPickerOpen(false)}
          onPick={(items) => {
            setPickerOpen(false);
            if (isNew) setPendingServer((p) => [...p, ...items.filter((i) => !p.some((x) => x.id === i.id))]);
            else fromExistingMut.mutate(items.map((i) => i.id));
          }} />
      )}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew} extraCards={statusCard || undefined}
          doc={{ type: DOC_TYPE, id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="ПН/РК і вид звернення"
        hint="Звернення до комісії ДПС щодо накладної або розрахунку коригування, реєстрацію яких зупинено">
        {isNew && !invoiceId && (
          <div className="doc-grid" style={{ marginBottom: '0.5rem' }}>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Організація *</label>
              <select value={effPickOrg} onChange={(e) => setPickOrgId(e.target.value)}>
                {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            {!form.manual && (
              <div className="field" style={{ gridColumn: 'span 8' }}>
                <label>ПН/РК — почніть вводити номер або покупця *</label>
                <SearchSelect
                  items={candidates.map((i: TaxInvoiceOutgoingListItem) => ({
                    id: i.id,
                    label: `${i.is_correction ? 'РК' : 'ПН'} № ${i.invoice_number} від ${fmtDate(i.invoice_date)} — ${i.contractor_name}`,
                    hint: ERPN_UK[i.erpn_status] ?? i.erpn_status,
                  }))}
                  value={invoiceId} onChange={(v) => setInvoiceId(v)}
                  placeholder={candQ.isLoading ? 'завантаження…' : candidates.length
                    ? '— номер ПН/РК або покупець —' : 'у цій організації ще немає ПН/РК'} />
              </div>
            )}
            <div className="field" style={{ gridColumn: form.manual ? 'span 8' : 'span 12', marginTop: form.manual ? 0 : '-0.25rem' }}>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontWeight: 400, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.manual}
                  onChange={(e) => setForm({ ...form, manual: e.target.checked })} />
                ПН/РК немає в системі — ввести реквізити вручну
              </label>
            </div>
          </div>
        )}
        {form.manual && (!isNew ? !docQ.data?.invoice_id : true) && (
          <div className="doc-grid" style={{ marginBottom: '0.5rem' }}>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Вид *</label>
              <select value={form.m_is_rk ? 'RK' : 'PN'} disabled={readOnly}
                onChange={(e) => setForm({ ...form, m_is_rk: e.target.value === 'RK' })}>
                <option value="PN">ПН</option>
                <option value="RK">РК</option>
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>№ ПН/РК *</label>
              <input value={form.m_number} disabled={readOnly} placeholder="як у квитанції"
                onChange={(e) => setForm({ ...form, m_number: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Дата складання *</label>
              <DatePopover value={form.m_date} disabled={readOnly}
                onChange={(v) => setForm({ ...form, m_date: v })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>ІПН покупця</label>
              <input value={form.m_ipn} disabled={readOnly} inputMode="numeric" maxLength={12}
                placeholder="12 цифр; 100000000000 — не видається покупцю"
                onChange={(e) => setForm({ ...form, m_ipn: e.target.value.replace(/\D/g, '') })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Покупець</label>
              <input value={form.m_buyer} disabled={readOnly}
                onChange={(e) => setForm({ ...form, m_buyer: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Обсяг без ПДВ, грн</label>
              <input value={form.m_base} disabled={readOnly} inputMode="decimal"
                onChange={(e) => setForm({ ...form, m_base: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Сума ПДВ, грн</label>
              <input value={form.m_vat} disabled={readOnly} inputMode="decimal"
                onChange={(e) => setForm({ ...form, m_vat: e.target.value })} />
            </div>
          </div>
        )}
        {!isNew && docQ.data?.manual_invoice && (
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
            Реквізити ПН/РК введено вручну (накладної немає в системі) — звірте їх із квитанцією про зупинення.
          </div>
        )}
        {inv && (
          <div style={{ fontSize: '0.9rem', marginBottom: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span>
              <b>{inv.is_correction ? 'РК' : 'ПН'} № {inv.invoice_number}</b> від {fmtDate(inv.invoice_date)}
              {' · '}{inv.contractor_name}
              {inv.contractor_inn ? ` (ІПН ${inv.contractor_inn})` : ''}
            </span>
            <span style={{ color: inv.erpn_status === 'REGISTERED' ? 'var(--badge-green-fg)' : 'var(--badge-amber-fg)' }}>
              {ERPN_UK[inv.erpn_status] ?? inv.erpn_status}
            </span>
            <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
              onClick={() => navigate(`/tax-invoices-outgoing/${inv.id}`)}>відкрити {inv.is_correction ? 'РК' : 'ПН'} →</a>
            {isNew && sp.get('invoice') == null && (
              <a style={{ cursor: 'pointer', color: 'var(--muted)' }} onClick={() => setInvoiceId('')}>обрати іншу</a>
            )}
          </div>
        )}
        {inv?.erpn_response && (
          <details style={{ fontSize: '0.85rem', marginBottom: '0.6rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>відповідь ДПС (квитанція)</summary>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{inv.erpn_response}</div>
          </details>
        )}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Вид звернення *</label>
            <select value={form.kind} disabled={!isNew}
              onChange={(e) => setForm({ ...form, kind: e.target.value as ErpnAppealKind })}>
              {(Object.keys(KIND_LABELS) as ErpnAppealKind[]).map((k) => (
                <option key={k} value={k}>{KIND_LABELS[k]}</option>))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата звернення *</label>
            <DatePopover value={form.document_date} disabled={readOnly}
              onChange={(v) => setForm({ ...form, document_date: v })} />
          </div>
          {!isComplaint ? (
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Реєстраційний № за квитанцією про зупинення</label>
              <input value={form.registration_number} disabled={readOnly} inputMode="numeric"
                placeholder="з квитанції №1 (графа 5 повідомлення)"
                onChange={(e) => setForm({ ...form, registration_number: e.target.value.replace(/\D/g, '') })} />
            </div>
          ) : (
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Рішення оскаржено в суді</label>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontWeight: 400, minHeight: '2rem' }}>
                <input type="checkbox" checked={form.court_appealed} disabled={readOnly}
                  onChange={(e) => setForm({ ...form, court_appealed: e.target.checked })} />
                так, подано позов (скаргу комісія тоді не розглядає)
              </label>
            </div>
          )}
          {isComplaint && (<>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>№ рішення комісії про відмову *</label>
              <input value={form.decision_number} disabled={readOnly}
                onChange={(e) => setForm({ ...form, decision_number: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Дата рішення *</label>
              <DatePopover value={form.decision_date} disabled={readOnly}
                onChange={(v) => setForm({ ...form, decision_date: v })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Участь у розгляді (відеоконференція)</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="checkbox" checked={form.video_participation} disabled={readOnly}
                  onChange={(e) => setForm({ ...form, video_participation: e.target.checked })} />
                <input value={form.video_person} disabled={readOnly || !form.video_participation}
                  placeholder="ПІБ учасника" style={{ flex: 1 }}
                  onChange={(e) => setForm({ ...form, video_person: e.target.value })} />
              </div>
            </div>
          </>)}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
          {isComplaint
            ? 'Скаргу подають протягом 10 робочих днів після рішення комісії регіонального рівня; розгляд — до 10 робочих днів (з продовженням до 30).'
            : form.kind === 'ADDITIONAL'
              ? 'Додаткові пояснення подають протягом 5 робочих днів після повідомлення комісії про їх потребу.'
              : 'Пояснення подають протягом 365 днів з дати ПН/РК; комісія вирішує за 5 робочих днів після отримання.'}
        </div>
      </DocBlock>

      <DocBlock index={2} title={isComplaint ? 'Причини незгоди з рішенням' : 'Пояснення'}
        hint={`До ${textMax} символів за бланком; довший текст — окремим документом у вкладеннях`}>
        <textarea rows={7} value={form.explanation_text} disabled={readOnly} style={{ width: '100%' }}
          placeholder={isComplaint
            ? 'Чому рішення про відмову є необґрунтованим: які документи надавались, що підтверджує реальність операції…'
            : 'Суть операції: що постачається, кому, за яким договором; чому операція реальна; які документи додаються…'}
          onChange={(e) => setForm({ ...form, explanation_text: e.target.value })} />
        <div style={{ fontSize: '0.8rem', textAlign: 'right',
          color: textLen > textMax ? 'var(--badge-red-fg)' : 'var(--muted)' }}>
          {textLen} / {textMax}
        </div>
      </DocBlock>

      <DocBlock index={3} title="Бухгалтерська довідка (пояснення для комісії)"
        hint="Лист на бланку, як його читає комісія: хто ми, суть операції, документи ланцюга, оплати, походження товару, що додаємо; іде у пакет окремою копією документа (PDF)"
        actions={!isNew && !readOnly ? (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button type="button" disabled={templateMut.isPending || draftMut.isPending}
              title="Заповнити з даних системи без агента; поточний текст довідки буде замінено"
              onClick={() => { if (!form.letter || letterIsTemplate || window.confirm('Замінити поточний текст довідки шаблоном з даних?')) templateMut.mutate(); }}>
              {templateMut.isPending ? '…' : 'заповнити з даних'}
            </button>
            <button type="button" className="primary" disabled={!canAi || draftMut.isPending || templateMut.isPending}
              title={canAi
                ? `Oblik+/AI складе довідку за зразком лише з даних системи (ПН/РК, документи, оплати, закупівлі, вкладення). Доступно кредитів: ${aiAvailable ?? '…'}`
                : 'Немає права «Oblik+/AI: складання пояснення до зупиненої ПН/РК» у цій організації'}
              onClick={() => draftMut.mutate()}>
              {draftMut.isPending ? 'складаємо…' : 'скласти агентом (Oblik+/AI)'}
            </button>
          </div>
        ) : undefined}>
        {isNew ? (
          <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Довідка складається після збереження звернення.</div>
        ) : !form.letter ? (
          <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>завантаження…</div>
        ) : (<>
          {letterIsTemplate && (
            <div style={{ fontSize: '0.8rem', color: 'var(--badge-amber-fg)', marginBottom: '0.4rem' }}>
              шаблон з даних системи — ще не збережено: відредагуйте, натисніть «скласти агентом» або «зберегти»
            </div>
          )}
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>вих. №</label>
              <input value={form.letter.number ?? ''} disabled={readOnly}
                onChange={(e) => setLetter({ number: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Дата довідки</label>
              <DatePopover value={form.letter.date ?? ''} disabled={readOnly}
                onChange={(v) => setLetter({ date: v || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 7' }}>
              <label>Заголовок (після «БУХГАЛТЕРСЬКА ДОВІДКА»)</label>
              <input value={form.letter.title ?? ''} disabled={readOnly}
                placeholder={letterQ.data?.title_default ?? 'з приводу блокування податкової накладної №… від …'}
                onChange={(e) => setLetter({ title: e.target.value || null })} />
            </div>
          </div>
          <div className="field">
            <label>Текст довідки (абзаци — через порожній рядок)</label>
            <textarea rows={14} value={paragraphsToText(form.letter.paragraphs)} disabled={readOnly}
              style={{ width: '100%' }}
              onChange={(e) => setLetter({ paragraphs: textToParagraphs(e.target.value) })} />
          </div>
          <div className="field">
            <label>ПРОСИМО:</label>
            <textarea rows={3} value={form.letter.request ?? ''} disabled={readOnly} style={{ width: '100%' }}
              onChange={(e) => setLetter({ request: e.target.value })} />
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Посада підписанта</label>
              <input value={form.letter.signer_title ?? ''} disabled={readOnly}
                onChange={(e) => setLetter({ signer_title: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Прізвище, імʼя, по батькові</label>
              <input value={form.letter.signer_name ?? ''} disabled={readOnly}
                onChange={(e) => setLetter({ signer_name: e.target.value })} />
            </div>
          </div>
          {(form.letter.notes?.length ?? 0) > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
              border: '1px solid var(--badge-amber-bd)', borderRadius: 6, padding: '0.4rem 0.75rem',
              fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              {form.letter.generated_by_ai ? 'Агент радить перевірити:' : 'Перевірте:'}
              <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                {form.letter.notes!.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => void previewPdf()}
              title="Зберегти і відкрити PDF довідки у новій вкладці">переглянути PDF</button>
            <button type="button" className="primary" disabled={readOnly || attachMut.isPending}
              title="Зберегти довідку і покласти її PDF до копій документів — піде у пакет окремим файлом"
              onClick={() => attachMut.mutate()}>
              {attachMut.isPending ? '…' : form.letter.attachment_id
                ? 'оновити PDF у копіях документів' : 'додати як копію документа (PDF)'}
            </button>
            {form.letter.attachment_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--badge-green-fg)' }}>✓ довідка серед копій документів</span>
            )}
          </div>
        </>)}
      </DocBlock>

      <DocBlock index={4} title="Копії документів"
        hint="PDF, JPG або PNG до 2 МБ кожен: договір, накладні, ТТН, акти, платіжки, ліцензії — кожна копія йде окремим файлом у пакеті">
        {isNew ? (<>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              style={{ display: 'none' }} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <button type="button" onClick={() => fileRef.current?.click()}>додати файли з компʼютера</button>
            <button type="button" disabled={!orgId} onClick={() => setPickerOpen(true)}
              title="Вкладення документів організації, що вже є на сервері: ПН, рахунок, реалізація, договір та інші">
              обрати з документів на сервері
            </button>
            <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>файли завантажаться одразу після збереження звернення</span>
          </div>
          {(pendingFiles.length + pendingServer.length) > 0 && (
            <TchTable style={{ marginTop: '0.5rem' }} data-testid="pending-attachments">
              <thead><tr><th>Файл</th><th>Звідки</th><th style={{ width: 80 }}>Розмір</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {pendingFiles.map((f, i) => (
                  <tr key={`f${i}`}>
                    <td>{f.name}</td><td>з компʼютера</td>
                    <td>{Math.max(1, Math.round(f.size / 1024))} КБ</td>
                    <td><button type="button" className="danger" onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))}>×</button></td>
                  </tr>
                ))}
                {pendingServer.map((a) => (
                  <tr key={a.id}>
                    <td>{a.filename}</td><td>{serverDocLabel(a)}</td>
                    <td>{Math.max(1, Math.round(a.size_bytes / 1024))} КБ</td>
                    <td><button type="button" className="danger" onClick={() => setPendingServer((p) => p.filter((x) => x.id !== a.id))}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
          )}
        </>) : (<>
          {!readOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
              <button type="button" disabled={fromExistingMut.isPending} onClick={() => setPickerOpen(true)}
                title="Вкладення документів організації, що вже є на сервері: ПН, рахунок, реалізація, договір та інші">
                {fromExistingMut.isPending ? '…' : 'обрати з документів на сервері'}
              </button>
            </div>
          )}
          <AttachmentsPanel organizationId={orgId} documentType={DOC_TYPE} documentId={id} />
          {(attQ.data?.length ?? 0) > 0 && (
            <TchTable style={{ marginTop: '0.5rem' }}>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th style={{ width: '32%' }}>Назва документа (для ДПС)</th>
                  <th style={{ width: 120 }}>№ документа</th>
                  <th style={{ width: 150 }}>Дата документа</th>
                </tr>
              </thead>
              <tbody>
                {(attQ.data ?? []).map((a) => {
                  const m = form.meta[a.id] ?? {};
                  const stem = a.filename.replace(/\.[A-Za-z0-9]{2,5}$/, '');
                  return (
                    <tr key={a.id}>
                      <td>{a.filename} <span style={{ color: 'var(--muted)' }}>({Math.ceil(a.size_bytes / 1024)} КБ)</span></td>
                      <td><input value={m.name ?? ''} placeholder={stem} disabled={readOnly}
                        onChange={(e) => setMeta(a.id, { name: e.target.value })} /></td>
                      <td><input value={m.number ?? ''} placeholder="б/н" disabled={readOnly}
                        onChange={(e) => setMeta(a.id, { number: e.target.value })} /></td>
                      <td><DatePopover value={m.date ?? ''} disabled={readOnly}
                        onChange={(v) => setMeta(a.id, { date: v || null })} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </TchTable>
          )}
        </>)}
      </DocBlock>

      <DocBlock index={5} title="Підписант">
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Хто підписує *</label>
            <select value={form.signer_kind} disabled={readOnly}
              onChange={(e) => setForm({ ...form, signer_kind: e.target.value as ErpnSignerKind })}>
              <option value="HEAD">{isFop ? 'підприємець' : 'керівник'}</option>
              <option value="AUTHORIZED">{isFop ? 'представник' : 'уповноважена особа'}</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>РНОКПП підписанта *</label>
            <input value={form.signer_inn} disabled={readOnly} inputMode="numeric" maxLength={10}
              onChange={(e) => setForm({ ...form, signer_inn: e.target.value.replace(/\D/g, '') })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 5' }}>
            <label>Прізвище, імʼя, по батькові *</label>
            <input value={form.signer_name} disabled={readOnly}
              onChange={(e) => setForm({ ...form, signer_name: e.target.value })} />
          </div>
        </div>
      </DocBlock>

      <DocBlock index={6} title="Коментар">
        <div className="field" style={{ margin: 0 }}>
          <input value={form.comment} disabled={readOnly} placeholder="внутрішня примітка (до ДПС не йде)"
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
